import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/ui/base/sheet';
import { ScrollArea } from '@/ui/base/scroll-area';
import { Button } from '@/ui/base/button';
import { Clock, RotateCcw } from 'lucide-react';
import { useDocsVersions } from '@/lib/useDocsVersions';

interface DocsVersionHistoryProps {
  articleId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Restaurar grava uma nova versão com o texto da escolhida. */
  onRestore: (numero: number) => void;
  restoring?: boolean;
}

export function DocsVersionHistory({
  articleId,
  open,
  onOpenChange,
  onRestore,
  restoring,
}: DocsVersionHistoryProps) {
  const { versions, loading, error, refetch, hasMore, loadingMore, loadMore } = useDocsVersions(
    articleId,
    open,
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" /> Histórico de Versões
          </SheetTitle>
          <SheetDescription>
            Cada salvamento deste artigo. Restaurar grava uma nova versão com o texto escolhido.
          </SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-120px)] mt-4">
          {loading && <p className="text-sm text-muted-foreground p-4">Carregando...</p>}
          {error ? (
            <div role="alert" className="p-4 space-y-2">
              <p className="text-sm text-destructive-text">
                Não foi possível carregar o histórico. Tente de novo.
              </p>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                Tentar de novo
              </Button>
            </div>
          ) : null}
          {!loading && !error && versions.length === 0 && (
            <p className="text-sm text-muted-foreground p-4">Nenhuma versão encontrada.</p>
          )}
          <ul className="space-y-3 pr-2">
            {versions.map((v) => (
              <li key={v.id} className="border border-border rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">
                    <strong className="text-foreground">Versão {v.numero}</strong>
                    {v.atual && ' (atual)'} · {new Date(v.created_at).toLocaleString('pt-BR')}
                  </span>
                  {!v.atual && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1 text-xs h-7"
                      disabled={restoring}
                      onClick={() => onRestore(v.numero)}
                      aria-label={`Restaurar a versão ${v.numero}`}
                    >
                      <RotateCcw className="h-3 w-3" /> Restaurar
                    </Button>
                  )}
                </div>
                <pre className="text-xs text-muted-foreground bg-muted rounded-lg p-2 max-h-32 overflow-auto whitespace-pre-wrap font-mono">
                  {v.previa || '(sem conteúdo)'}
                  {v.caracteres > v.previa.length && '...'}
                </pre>
              </li>
            ))}
          </ul>
          {hasMore && (
            <div className="p-3">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={loadingMore}
                onClick={loadMore}
              >
                {loadingMore ? 'Carregando...' : 'Carregar versões mais antigas'}
              </Button>
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
